import type {
  GetContractsResponse,
  GetContractDetailResponse,
  GetDashboardInfoResponse,
  AccountManager,
  InsuranceCasesOverview,
  Notice,
  GetClientsForCustomerResponse,
  GetClientInformationResponse,
  Client,
  ClientInformation,
  InsuranceCaseComs,
  Contract,
  ContractDetail,
  GetDocumentsResponse,
  Document,
  GetSubcontractsResponse,
  GetInsuranceCasesResponse,
  InsuranceCase,
  GetPaymentsResponse,
  Payment,
  GetSearchResponse,
  GetInsuranceCaseDetailResponse,
  GetMessagesResponse,
  InsuranceCaseDetail,
  Participant,
  Message,
  Settlement,
} from '@/types/customer'
import type { PostMessageResponse, SetMessageStatusResponse } from '@/types/message'
import type { CustomerDataService } from '@/types/customer-data-service'
import { SearchTarget } from '@/types/search'
import {
  ContractValidity,
  ContractType,
  ContractStatus,
  InsuranceCaseStatus,
  MessageType,
  MessageStatus,
} from '@/types/customer'

// Mock account manager data
const mockAccountManager: AccountManager = {
  name: 'Jan Novák',
  email: 'jan.novak@example.com',
  phone: '+420 123 456 789',
  initials: 'JN',
  classification: 'Senior Account Manager',
  classificationType: undefined,
}

// Mock contract data for demonstration
const mockContracts: Contract[] = [
  {
    id: 1,
    contractNumber: 'CTR-2024-001',
    startDate: new Date('2024-01-01'),
    endDate: new Date('2024-12-31'),
    partner: 'První česká',
    segment: 'autopojištění',
    subsegment: 'osobní automobily',
    product: 'havarijní',
    attributes: [
      {
        name: 'RZ',
        value: 'EL058569',
        toolTip: '',
      },
      {
        name: 'VIN',
        value: '1597536984563',
        toolTip: '',
      },
    ],
    validity: ContractValidity.term,
    type: ContractType.multi,
    status: ContractStatus.active,
    cancellationReason: null,
  },
  {
    id: 2,
    contractNumber: 'CTR-2024-002',
    startDate: new Date('2024-02-01'),
    endDate: new Date('2025-01-31'),
    partner: 'Nějaká jiná',
    segment: 'autopojištění',
    subsegment: 'osobní automobily',
    product: 'povinné',
    attributes: [
      {
        name: 'RZ',
        value: 'EL058569',
        toolTip: '',
      },
      {
        name: 'VIN',
        value: '1597536984563',
        toolTip: '',
      },
    ],
    validity: ContractValidity.term,
    type: ContractType.multi,
    status: ContractStatus.active,
    cancellationReason: null,
  },
  {
    id: 3,
    contractNumber: 'CTR-2024-003',
    startDate: new Date('2024-03-01'),
    endDate: new Date('2024-08-31'),
    partner: 'Nějaká jiná',
    segment: 'autopojištění',
    subsegment: 'osobní automobily',
    product: 'rámcová',
    attributes: [
      {
        name: 'Počet smluv',
        value: '8',
        toolTip: '',
      },
    ],
    validity: ContractValidity.term,
    type: ContractType.multi,
    status: ContractStatus.active,
    cancellationReason: null,
  },
]

// Mock contract detail data for demonstration
const mockContractDetails: ContractDetail[] = [
  {
    id: 1,
    contractNumber: 'CTR-2024-001',
    startDate: new Date('2024-01-01'),
    endDate: new Date('2024-12-31'),
    partner: 'První česká',
    premium: 15000,
    renewalDate: new Date('2024-12-01'),
    subsegment: 'osobní automobily',
    policyholder: 'Jan Novák',
    risks: [
      {
        description: 'Havarijní pojištění vozidla',
        limit: 'Bez limitu',
        amount: 500000,
        share: '0%',
        scope: 'Celé vozidlo',
      },
    ],
    product: 'havarijní',
    attributes: [
      {
        name: 'RZ',
        value: 'EL058569',
        toolTip: 'Registrační značka vozidla',
      },
      {
        name: 'VIN',
        value: '1597536984563',
        toolTip: 'VIN kód vozidla',
      },
    ],
    type: ContractType.multi,
    annualPremium: 15000,
    paymentFrequency: 'Ročně',
    status: ContractStatus.active,
    cancellationReason: '',
    accountManager: [mockAccountManager],
    segment: 'autopojištění',
    validity: ContractValidity.term,
  },
]

const mockClients: Client[] = [
  { id: 1, name: 'Client 1' },
  { id: 2, name: 'Client 2' },
  { id: 3, name: 'Client 3' },
]

const mockClientInformation: Record<number, ClientInformation> = {
  1: {
    name: 'Client 1',
    type: 'fyzická osoba',
    ic: '',
    birthDate: new Date('1987-02-14'),
    phones: ['+420 123 456 789', '+420 222 333 444', '+420 777 888 999'],
    emails: ['client1@example.com', 'client1.work@example.com', 'client1.alt@example.com'],
    addresses: [
      {
        type: 'trvalé bydliště',
        street: 'Václavské náměstí 1/10',
        city: 'Praha',
        zipCode: '11000',
      },
      {
        type: 'korespondenční adresa',
        street: 'Jungmannova 12',
        city: 'Praha',
        zipCode: '11000',
      },
      {
        type: 'přechodné bydliště',
        street: 'Lidická 45',
        city: 'Brno',
        zipCode: '60200',
      },
    ],
  },
  2: {
    name: 'Client 2',
    type: 'právnická osoba',
    ic: '87654321',
    birthDate: null,
    phones: ['+420 987 654 321'],
    emails: ['info@client2.example'],
    addresses: [
      {
        type: 'sídlo firmy',
        street: 'Masarykova 25',
        city: 'Brno',
        zipCode: '60200',
      },
    ],
  },
  3: {
    name: 'Client 3',
    type: 'fyzická osoba',
    ic: '',
    birthDate: new Date('1992-06-08'),
    phones: ['+420 555 444 333'],
    emails: ['client3@example.com'],
    addresses: [
      {
        type: 'trvalé bydliště',
        street: 'Křižíkova 42',
        city: 'Plzeň',
        zipCode: '30100',
      },
    ],
  },
}

const mockInsuranceCasesOverview: InsuranceCasesOverview[] = [
  { segment: 'autopojištění', count: 2 },
  { segment: 'havarijní pojištění', count: 1 },
]

const mockNoticeBoard: Notice[] = [
  { id: 1, severity: 1, description: 'Mock notice for vehicle damage' },
  { id: 2, severity: 2, description: 'Mock notice for property damage' },
  { id: 3, severity: 3, description: 'Mock notice for medical expenses' },
  { id: 4, severity: 4, description: 'Mock notice for financial loss' },
  { id: 5, severity: 5, description: 'Mock notice for legal issues' },
]

const mockMessageBoard: Message[] = [
  {
    id: 1,
    description: 'Mock message for vehicle damage',
    status: MessageStatus.read,
    startDate: new Date('2024-01-15'),
    type: MessageType.received,
  },
  {
    id: 2,
    description: 'Mock message for property damage',
    status: MessageStatus.created,
    startDate: new Date('2024-01-10'),
    type: MessageType.sent,
  },
  {
    id: 3,
    description: 'Mock message for medical expenses',
    status: MessageStatus.archived,
    startDate: new Date('2024-01-05'),
    type: MessageType.received,
  },
]

const mockDocuments: Document[] = [
  {
    id: 1,
    uid: '1234567890',
    name: 'Insurance Policy Document',
    extension: 'pdf',
    size: 1024000,
    note: 'Main insurance policy document',
  },
  {
    id: 2,
    uid: '1234567891',
    name: 'Terms and Conditions',
    extension: 'pdf',
    size: 512000,
    note: 'Terms and conditions document',
  },
  {
    id: 3,
    uid: '1234567892',
    name: 'Coverage Details',
    extension: 'docx',
    size: 256000,
    note: 'Detailed coverage information',
  },
]

const mockInsuranceCases: InsuranceCase[] = [
  {
    id: 1,
    contractType: ContractType.multi,
    contractId: 1,
    subcontractId: null,
    caseNumber: 'IC-2024-001',
    reportNumber: 'RPT-2024-001',
    status: InsuranceCaseStatus.open,
    date: new Date('2024-01-15'),
    endDate: null,
    uid: 'IC-2024-001-UID',
    segment: 'autopojištění',
    partner: 'První česká',
    representationType: 'Pojištěný',
    damageType: 'HAV',
    RZ: 'EL058569',
    Vin: '1597536984563',
    accountManager: [mockAccountManager],
    location: 'Praha 1, Václavské náměstí',
    cause: 'Kolize',
    description: 'Pojistná událost při parkování',
    regress: false,
    reportDate: new Date('2024-01-15'),
  },
  {
    id: 2,
    contractType: ContractType.multi,
    contractId: 1,
    subcontractId: null,
    caseNumber: 'IC-2024-002',
    reportNumber: 'RPT-2024-002',
    status: InsuranceCaseStatus.closed,
    date: new Date('2024-02-10'),
    endDate: new Date('2024-02-25'),
    uid: 'IC-2024-002-UID',
    segment: 'autopojištění',
    partner: 'První česká',
    representationType: 'Pojištěný',
    damageType: 'POV',
    RZ: 'EL058569',
    Vin: '1597536984563',
    accountManager: [mockAccountManager],
    location: 'Brno, Náměstí Svobody',
    cause: 'Dopravní nehoda',
    description: 'Pojistná událost při dopravní nehodě',
    regress: false,
    reportDate: new Date('2024-02-10'),
  },
  {
    id: 3,
    contractType: ContractType.multi,
    contractId: 2,
    subcontractId: null,
    caseNumber: 'IC-2024-003',
    reportNumber: 'RPT-2024-003',
    status: InsuranceCaseStatus.postponed,
    date: new Date('2024-03-05'),
    endDate: null,
    uid: 'IC-2024-003-UID',
    segment: 'autopojištění',
    partner: 'Nějaká jiná',
    representationType: 'Poškozený',
    damageType: 'Majetek',
    RZ: null,
    Vin: null,
    accountManager: [mockAccountManager],
    location: 'Ostrava, Hlavní třída',
    cause: 'Vandalismus',
    description: 'Pojistná událost - vandalismus',
    regress: true,
    reportDate: new Date('2024-03-05'),
  },
]

const mockPayments: Payment[] = [
  {
    paymentDate: new Date('2024-01-15'),
    periodFrom: new Date('2024-01-01'),
    periodTo: new Date('2024-01-31'),
    amount: 15000,
    note: 'Monthly premium payment',
  },
  {
    paymentDate: new Date('2024-02-15'),
    periodFrom: new Date('2024-02-01'),
    periodTo: new Date('2024-02-29'),
    amount: 15000,
    note: 'Monthly premium payment',
  },
  {
    paymentDate: new Date('2024-03-15'),
    periodFrom: new Date('2024-03-01'),
    periodTo: new Date('2024-03-31'),
    amount: 15000,
    note: 'Monthly premium payment',
  },
]

export class MockCustomerDataService implements CustomerDataService {
  async getSubcontractDocuments(
    clientId: number,
    subcontractId: number,
  ): Promise<GetDocumentsResponse> {
    console.log('getSubcontractDocuments Mock', { clientId, subcontractId })
    // Simulate API delay
    await this.delay(300)
    return {
      success: true,
      documents: mockDocuments,
    }
  }

  async getInsuranceCaseDocuments(clientId: number, caseId: number): Promise<GetDocumentsResponse> {
    console.log('getInsuranceCaseDocuments Mock', { clientId, caseId })
    // Simulate API delay
    await this.delay(300)
    return {
      success: true,
      documents: mockDocuments,
    }
  }

  async getGeneralAttachments(): Promise<GetDocumentsResponse> {
    console.log('getGeneralAttachments Mock')
    await this.delay(300)
    return {
      success: true,
      documents: mockDocuments,
    }
  }

  async getClientAttachments(clientId: number): Promise<GetDocumentsResponse> {
    console.log('getClientAttachments Mock', { clientId })
    await this.delay(300)
    return {
      success: true,
      documents: mockDocuments,
    }
  }

  async getSubcontractDetail(
    clientId: number,
    subcontractId: number,
  ): Promise<GetContractDetailResponse> {
    console.log('getSubcontractDetail Mock', { clientId, subcontractId })
    // Simulate API delay
    await this.delay(600)

    try {
      // Find subcontract detail by ID (using subcontract IDs from getSubcontracts)
      const mockSubcontractDetails: ContractDetail[] = [
        {
          id: 101,
          contractNumber: 'SUB-2024-001',
          startDate: new Date('2024-01-01'),
          endDate: new Date('2024-12-31'),
          partner: 'První česká',
          premium: 12000,
          renewalDate: new Date('2024-12-01'),
          subsegment: 'osobní automobily',
          policyholder: 'Jan Novák',
          risks: [
            {
              description: 'Havarijní pojištění vozidla',
              limit: 'Bez limitu',
              amount: 400000,
              share: '0%',
              scope: 'Celé vozidlo',
            },
          ],
          product: 'havarijní',
          attributes: [
            {
              name: 'RZ',
              value: 'EL058569',
              toolTip: 'Registrační značka vozidla',
            },
            {
              name: 'VIN',
              value: '1597536984563',
              toolTip: 'VIN kód vozidla',
            },
          ],
          type: ContractType.subcontract,
          annualPremium: 12000,
          paymentFrequency: 'Ročně',
          status: ContractStatus.active,
          cancellationReason: null,
          accountManager: [mockAccountManager],
          segment: 'autopojištění',
          validity: ContractValidity.term,
        },
        {
          id: 102,
          contractNumber: 'SUB-2024-002',
          startDate: new Date('2024-02-01'),
          endDate: new Date('2024-11-30'),
          partner: 'První česká',
          premium: 8000,
          renewalDate: new Date('2024-11-01'),
          subsegment: 'osobní automobily',
          policyholder: 'Jan Novák',
          risks: [
            {
              description: 'Povinné ručení',
              limit: 'Bez limitu',
              amount: 5000000,
              share: '0%',
              scope: 'Celé vozidlo',
            },
          ],
          product: 'povinné',
          attributes: [
            {
              name: 'RZ',
              value: 'EL058570',
              toolTip: 'Registrační značka vozidla',
            },
            {
              name: 'VIN',
              value: '1597536984564',
              toolTip: 'VIN kód vozidla',
            },
          ],
          type: ContractType.subcontract,
          annualPremium: 8000,
          paymentFrequency: 'Ročně',
          status: ContractStatus.active,
          cancellationReason: null,
          accountManager: [mockAccountManager],
          segment: 'autopojištění',
          validity: ContractValidity.term,
        },
      ]

      const subcontractDetail = mockSubcontractDetails.find(
        (contract) => contract.id === subcontractId,
      )

      if (!subcontractDetail) {
        return {
          success: false,
          message: 'Subcontract not found',
        }
      }

      return {
        success: true,
        contract: subcontractDetail,
      }
    } catch (error) {
      console.error('Error fetching subcontract detail:', error)
      return {
        success: false,
        message: 'Failed to fetch subcontract detail',
      }
    }
  }

  getDocumentUrl(documentUid: string): Promise<string> {
    return Promise.resolve(`https://example.com/document/${documentUid}`)
  }

  async getUploadUrl(): Promise<string> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 100))
    return Promise.resolve('https://example.com/api/upload')
  }

  async deleteUploadedFile(uid: string): Promise<{ success: boolean; message?: string }> {
    console.log('deleteUploadedFile Mock', uid)
    // Simulate API delay
    await this.delay(200)
    return { success: true }
  }

  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<{ success: boolean; message?: string }> {
    console.log('changePassword Mock', {
      hasCurrentPassword: Boolean(currentPassword),
      hasNewPassword: Boolean(newPassword),
    })
    await this.delay(200)
    return { success: true }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  async getDashboardInfo(clientId: number): Promise<GetDashboardInfoResponse> {
    console.log('getDashboardInfo Mock', clientId)
    // Simulate API delay
    await this.delay(2000)

    const segmentCounts = mockContracts.reduce((acc: Map<string, number>, contract) => {
      const segment = contract.segment
      acc.set(segment, (acc.get(segment) || 0) + 1)
      return acc
    }, new Map<string, number>())

    return {
      success: true,
      dashboard: {
        accountManager: [mockAccountManager],
        contractsOverview: Array.from(segmentCounts.entries()).map(([segment, count]) => ({
          segment,
          count,
        })),
        insurancesOverview: mockInsuranceCasesOverview,
        noticeBoard: mockNoticeBoard,
        messagesOverview: [
          { type: MessageType.sent, count: 5 },
          { type: MessageType.received, count: 3 },
        ],
      },
    }
  }

  async getClientsForCustomer(): Promise<GetClientsForCustomerResponse> {
    console.log('getClientsForCustomer Mock')
    // Simulate API delay
    await this.delay(200)
    return { success: true, clients: mockClients }
  }

  async getClientInformation(clientId: number): Promise<GetClientInformationResponse> {
    console.log('getClientInformation Mock', clientId)
    await this.delay(200)
    const clientInformation = mockClientInformation[clientId]
    if (!clientInformation) {
      return { success: false, message: 'Client information not found' }
    }

    return { success: true, clientInformation }
  }

  async getContracts(
    clientId: number,
    active: boolean = true,
    expired: boolean = false,
    canceled: boolean = false,
  ): Promise<GetContractsResponse> {
    console.log('getContracts Mock', clientId, { active, expired, canceled })
    // Simulate API delay
    await this.delay(400)

    try {
      // Filter contracts by customer ID
      const customerContracts = clientId == 1 ? mockContracts : []

      if (customerContracts.length === 0) {
        return {
          success: true,
          contracts: [],
          message: 'No contracts found for this customer',
        }
      }

      return {
        success: true,
        contracts: customerContracts,
      }
    } catch (error) {
      console.error('Error fetching contracts:', error)
      return {
        success: false,
        message: 'Failed to fetch contracts',
      }
    }
  }

  async getContractDetail(
    clientId: number,
    contractId: number,
    type: ContractType,
  ): Promise<GetContractDetailResponse> {
    console.log('getContractDetail Mock', { clientId, contractId, type })
    // Simulate API delay
    await this.delay(600)

    try {
      // Find contract detail by ID
      const contractDetail = mockContractDetails.find((contract) => contract.id === contractId)

      if (!contractDetail) {
        return {
          success: false,
          message: 'Contract not found',
        }
      }

      return {
        success: true,
        contract: contractDetail,
      }
    } catch (error) {
      console.error('Error fetching contract detail:', error)
      return {
        success: false,
        message: 'Failed to fetch contract detail',
      }
    }
  }

  async getContractDocuments(clientId: number, contractId: number): Promise<GetDocumentsResponse> {
    console.log('getContractDocuments Mock', { clientId, contractId })
    // Simulate API delay
    await this.delay(300)

    try {
      // Filter documents by contract ID
      const contractDocuments = contractId === 1 ? mockDocuments : []

      if (contractDocuments.length === 0) {
        return {
          success: true,
          documents: [],
          message: 'No documents found for this contract',
        }
      }

      return {
        success: true,
        documents: contractDocuments,
      }
    } catch (error) {
      console.error('Error fetching contract documents:', error)
      return {
        success: false,
        message: 'Error fetching contract documents.',
      }
    }
  }

  async getMessageAttachments(clientId: number, messageId: number): Promise<GetDocumentsResponse> {
    console.log('getMessageAttachments Mock', { clientId, messageId })
    // Simulate API delay
    await this.delay(300)

    try {
      // Filter documents by message ID - use mockDocuments for messageId === 1
      const messageDocuments = messageId === 1 ? mockDocuments : []

      if (messageDocuments.length === 0) {
        return {
          success: true,
          documents: [],
          message: 'No attachments found for this message',
        }
      }

      return {
        success: true,
        documents: messageDocuments,
      }
    } catch (error) {
      console.error('Error fetching message attachments:', error)
      return {
        success: false,
        message: 'Error fetching message attachments.',
      }
    }
  }

  async getSubcontracts(
    clientId: number,
    contractId: number,
    active: boolean = true,
    expired: boolean = false,
    canceled: boolean = false,
  ): Promise<GetSubcontractsResponse> {
    console.log('getSubcontracts Mock', { clientId, contractId, active, expired, canceled })
    // Simulate API delay
    await this.delay(300)

    try {
      // Mock subcontracts data - only for contract ID 1 (multi contract)
      let mockSubcontracts: Contract[] =
        contractId === 1
          ? [
              {
                id: 101,
                contractNumber: 'SUB-2024-001',
                startDate: new Date('2024-01-01'),
                endDate: new Date('2024-12-31'),
                partner: 'První česká',
                segment: 'autopojištění',
                subsegment: 'osobní automobily',
                product: 'havarijní',
                attributes: [
                  { name: 'RZ', value: 'EL058569', toolTip: '' },
                  { name: 'VIN', value: '1597536984563', toolTip: '' },
                ],
                validity: ContractValidity.term,
                type: ContractType.subcontract,
                status: ContractStatus.active,
                parentContractId: 1,
                parentContractType: ContractType.multi,
                cancellationReason: null,
              },
              {
                id: 102,
                contractNumber: 'SUB-2024-002',
                startDate: new Date('2024-02-01'),
                endDate: new Date('2024-11-30'),
                partner: 'První česká',
                segment: 'autopojištění',
                subsegment: 'osobní automobily',
                product: 'povinné',
                attributes: [
                  { name: 'RZ', value: 'EL058570', toolTip: '' },
                  { name: 'VIN', value: '1597536984564', toolTip: '' },
                ],
                validity: ContractValidity.term,
                type: ContractType.subcontract,
                status: ContractStatus.active,
                parentContractId: 1,
                parentContractType: ContractType.multi,
                cancellationReason: null,
              },
            ]
          : []

      // Filter by status based on parameters
      if (!active) {
        mockSubcontracts = mockSubcontracts.filter((c) => c.status !== ContractStatus.active)
      }
      if (!expired) {
        mockSubcontracts = mockSubcontracts.filter((c) => c.status !== ContractStatus.inactive)
      }
      if (!canceled) {
        mockSubcontracts = mockSubcontracts.filter((c) => c.status !== ContractStatus.cancelled)
      }

      if (mockSubcontracts.length === 0) {
        return {
          success: true,
          subcontracts: [],
          message: 'No subcontracts found for this contract',
        }
      }

      return {
        success: true,
        subcontracts: mockSubcontracts,
      }
    } catch (error) {
      console.error('Error fetching subcontracts:', error)
      return {
        success: false,
        message: 'Failed to fetch subcontracts',
      }
    }
  }

  async getInsuranceCases(
    clientId: number,
    contractId?: number,
    type?: ContractType,
    filters?: { open: boolean; closed: boolean; postponed: boolean },
  ): Promise<GetInsuranceCasesResponse> {
    console.log('getInsuranceCases Mock', { clientId, contractId, type, filters })
    // Simulate API delay
    await this.delay(300)

    try {
      // Mock insurance cases data - only for contract ID 1
      let mockCases: InsuranceCase[] = mockInsuranceCases
      if (contractId) {
        mockCases = mockCases.filter((ic) => ic.contractId === contractId)
      }

      // Apply status filters if provided
      if (filters) {
        mockCases = mockCases.filter((ic) => {
          if (ic.status === InsuranceCaseStatus.open && !filters.open) return false
          if (ic.status === InsuranceCaseStatus.closed && !filters.closed) return false
          if (ic.status === InsuranceCaseStatus.postponed && !filters.postponed) return false
          return true
        })
      }

      if (mockCases.length === 0) {
        return {
          success: true,
          insuranceCases: [],
          message: 'No insurance cases found',
        }
      }

      return {
        success: true,
        insuranceCases: mockCases,
      }
    } catch (error) {
      console.error('Error fetching insurance cases:', error)
      return {
        success: false,
        message: 'Failed to fetch insurance cases',
      }
    }
  }

  async getPayments(clientId: number, contractId: number): Promise<GetPaymentsResponse> {
    console.log('getPayments Mock', { clientId, contractId })
    // Simulate API delay
    await this.delay(300)

    try {
      // Mock payments data - only for contract ID 1
      const mockPaymentsData: Payment[] = contractId === 1 ? mockPayments : []

      if (mockPaymentsData.length === 0) {
        return {
          success: true,
          payments: [],
          message: 'No payments found for this contract',
        }
      }

      return {
        success: true,
        payments: mockPaymentsData,
      }
    } catch (error) {
      console.error('Error fetching payments:', error)
      return {
        success: false,
        message: 'Failed to fetch payments',
      }
    }
  }

  async getInsuranceCaseDetail(
    clientId: number,
    caseId: number,
  ): Promise<GetInsuranceCaseDetailResponse> {
    console.log('getInsuranceCaseDetail Mock', { clientId, caseId })
    // Simulate API delay
    await this.delay(400)

    try {
      // Find insurance case by ID
      const baseCase = mockInsuranceCases.find((ic) => ic.id === caseId)

      if (!baseCase) {
        return {
          success: false,
          message: 'Insurance case not found',
        }
      }

      // Create mock detail with additional fields
      const mockSettlement: Settlement = {
        damageEstimate: 50000,
        coParticipation: 5000,
        totalDamage: 55000,
        reserve: 60000,
        fulfillmentTotal: 52000,
      }

      const mockParticipants: Participant[] = [
        {
          id: 1,
          type: 'Poškozený',
          name: 'Jan Novák',
          address: 'Praha 1, Václavské náměstí 1',
          phone: '+420 123 456 789',
          email: 'jan.novak@example.com',
          note: 'Hlavní poškozený',
        },
        {
          id: 2,
          type: 'Škodce',
          name: 'Petr Svoboda',
          address: 'Brno, Náměstí Svobody 1',
          phone: '+420 987 654 321',
          email: 'petr.svoboda@example.com',
          note: 'Způsobil škodu',
        },
      ]

      const mockMessages: InsuranceCaseComs[] = [
        {
          id: 1,
          text: 'První kontakt s klientem ohledně pojistné události',
          date: new Date('2024-01-15'),
          note: 'Telefonický hovor',
        },
        {
          id: 2,
          text: 'Zaslání dokumentace k pojistné události',
          date: new Date('2024-01-16'),
          note: 'Email',
        },
        {
          id: 3,
          text: 'Vyhodnocení škody a stanovení výše náhrady',
          date: new Date('2024-01-20'),
          note: 'Interní poznámka',
        },
      ]

      const insuranceCaseDetail: InsuranceCaseDetail = {
        ...baseCase,
        participants: mockParticipants,
        communication: mockMessages,
        settlement: mockSettlement,
        notes: [
          'Pojistná událost byla nahlášena včas',
          'Probíhá šetření příčiny škody',
          'Čeká se na vyjádření pojišťovny',
        ],
        payments: [],
      }

      return {
        success: true,
        insuranceCase: insuranceCaseDetail,
      }
    } catch (error) {
      console.error('Error fetching insurance case detail:', error)
      return {
        success: false,
        message: 'Failed to fetch insurance case detail',
      }
    }
  }

  async search(
    clientId: number,
    text: string,
    target?: SearchTarget | null,
  ): Promise<GetSearchResponse> {
    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500))

      const searchText = text.toLowerCase()

      // Filter contracts that match the search text
      let matchingContracts = mockContracts.filter(
        (contract) =>
          contract.contractNumber.toLowerCase().includes(searchText) ||
          contract.partner.toLowerCase().includes(searchText) ||
          contract.segment.toLowerCase().includes(searchText) ||
          contract.product.toLowerCase().includes(searchText),
      )

      // Filter insurance cases that match the search text
      let matchingInsuranceCases = mockInsuranceCases.filter(
        (case_) =>
          case_.caseNumber.toLowerCase().includes(searchText) ||
          case_.reportNumber.toLowerCase().includes(searchText) ||
          (case_.partner && case_.partner.toLowerCase().includes(searchText)) ||
          (case_.description && case_.description.toLowerCase().includes(searchText)) ||
          (case_.segment && case_.segment.toLowerCase().includes(searchText)),
      )

      // Apply target filter if provided
      if (target === SearchTarget.contracts) {
        matchingInsuranceCases = []
      } else if (target === SearchTarget.insuranceCases) {
        matchingContracts = []
      }
      // If target is null, undefined, or 'all', return both

      return {
        success: true,
        contracts: matchingContracts,
        insuranceCases: matchingInsuranceCases,
      }
    } catch (error) {
      console.error('Error performing search:', error)
      return {
        success: false,
        message: 'Failed to perform search',
      }
    }
  }

  async submitMessage(
    clientId: number,
    title: string,
    text: string,
    contractType: ContractType | null,
    contractId: number | null,
    caseId: number | null,
    attachments: Array<{ fileGuid: string; fileName: string }>,
  ): Promise<PostMessageResponse> {
    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500))

      console.log('Mock submitMessage', {
        clientId,
        title,
        text,
        contractType,
        contractId,
        caseId,
        attachments,
      })

      // Simulate successful submission
      return {
        success: true,
        messageId: Math.floor(Math.random() * 10000),
      }
    } catch (error) {
      console.error('Error submitting message:', error)
      return {
        success: false,
        message: 'Failed to submit message',
      }
    }
  }

  async setMessageStatus(
    clientId: number,
    messageId: number,
    status: MessageStatus,
  ): Promise<SetMessageStatusResponse> {
    console.log('setMessageStatus Mock', { clientId, messageId, status })
    // Simulate API delay
    await this.delay(300)

    try {
      // Find and update the message in mockMessageBoard if it exists
      const messageIndex = mockMessageBoard.findIndex((msg) => msg.id === messageId)
      if (messageIndex !== -1) {
        mockMessageBoard[messageIndex].status = status
      }

      return {
        success: true,
        changedMessage: mockMessageBoard.find((msg) => msg.id === messageId),
      }
    } catch (error) {
      console.error('Error setting message status:', error)
      return {
        success: false,
        message: 'Error setting message status.',
      }
    }
  }

  async getMessages(clientId: number, archived?: boolean): Promise<GetMessagesResponse> {
    console.log('getMessages Mock', clientId)
    // Simulate API delay
    await this.delay(500)

    let messages = mockMessageBoard
    if (archived !== undefined) {
      messages = messages.filter((msg) => {
        const isArchived = msg.status === MessageStatus.archived
        return archived ? isArchived : !isArchived
      })
    }

    return {
      success: true,
      messages: messages,
    }
  }

  async getContractMessages(clientId: number, contractId: number): Promise<GetMessagesResponse> {
    console.log('getContractMessages Mock', clientId, contractId)
    // Simulate API delay
    await this.delay(500)

    return {
      success: true,
      messages: mockMessageBoard,
    }
  }

  async getSubcontractMessages(
    clientId: number,
    subcontractId: number,
  ): Promise<GetMessagesResponse> {
    console.log('getSubcontractMessages Mock', clientId, subcontractId)
    // Simulate API delay
    await this.delay(500)

    return {
      success: true,
      messages: mockMessageBoard,
    }
  }

  async getInsuranceCaseMessages(clientId: number, caseId: number): Promise<GetMessagesResponse> {
    console.log('getInsuranceCaseMessages Mock', clientId, caseId)
    // Simulate API delay
    await this.delay(500)

    return {
      success: true,
      messages: mockMessageBoard,
    }
  }
}
